<script setup lang="ts">
import { computed, ref, reactive, nextTick } from 'vue';
import { kdm } from '@/store/kdm';
import Dialog from 'primevue/dialog';
import MultiSelect from 'primevue/multiselect';
import { useFetch } from '@/composables/useFetch';

const visible = ref(false);
const assignments = reactive<Record<string, string[]>>({});
const ofetch = useFetch();
const saving = ref(false);

// Members list for MultiSelect - ensure IDs are strings for proper comparison
const members = computed(() => {
  return (kdm.value?.group_members || []).map(m => ({
    ...m,
    id: String(m.id)
  }));
});

// Open modal function exposed to parent
function open() {
  if (!kdm.value) return;

  // Load existing assignments from matrix data
  // Handle both string and object formats if backend returns parsed or unparsed
  let matrixData: any = {};
  if (kdm.value.field_matrix_data) {
    if (typeof kdm.value.field_matrix_data === 'string') {
      try {
        matrixData = JSON.parse(kdm.value.field_matrix_data) || {};
      } catch (e) {
        console.error("Invalid JSON", e);
        matrixData = {};
      }
    } else {
      matrixData = kdm.value.field_matrix_data || {};
    }
  }

  const currentAssignments = matrixData?.assignments || {};

  // Clear existing assignments (reactive object)
  Object.keys(assignments).forEach(key => delete assignments[key]);

  // Initialize for each Stakeholder
  const stakeHolders = kdm.value.stake_holders || [];
  stakeHolders.forEach(sh => {
    // Ensure consistent string key for both lookup and storage
    const shId = String(sh.id);
    // Look up assignments with string key - server might store with different key type
    const assignedIds = currentAssignments[shId] || currentAssignments[sh.id] || [];
    // Store with string key and ensure member IDs are also strings for MultiSelect
    assignments[shId] = assignedIds.map((id: any) => String(id));
  });

  visible.value = true;
}

async function save() {
  if (!kdm.value) return;

  // Close any open dropdown by blurring active element
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }

  // Wait for Vue to process any pending updates from MultiSelect
  await nextTick();

  saving.value = true;

  try {
    // Prepare the payload - create a plain copy of the reactive object
    const assignmentsCopy: Record<string, string[]> = {};
    Object.keys(assignments).forEach(key => {
      assignmentsCopy[key] = [...assignments[key]];
    });

    const payload = { assignments: assignmentsCopy };

    const response = await ofetch<any>(`/epbapi/kdm/${kdm.value.nid}/`, {
      method: 'PATCH',
      body: payload,
    });

    // Update local store with the response
    if (response.data) {
      kdm.value.field_matrix_data = response.data;
    }

    visible.value = false;
  } catch (e) {
    alert("Error saving assignments");
    console.error(e);
  } finally {
    saving.value = false;
  }
}

defineExpose({ open });
</script>

<template>
  <Dialog v-model:visible="visible" header="Manage Stakeholder Assignments" modal :style="{ width: '50vw' }">
    <div class="p-fluid">
      <p class="text-muted small mb-3">
        Assign group members to Stakeholders. Only assigned members (and Admins/Owner) can edit data for that Stakeholder.
      </p>
      
      <div v-for="sh in kdm?.stake_holders" :key="sh.id" class="mb-3 border-bottom pb-3">
        <label class="fw-bold d-block mb-1">{{ sh.title }}</label>
        <MultiSelect 
          v-model="assignments[sh.id]" 
          :options="members" 
          optionLabel="name" 
          optionValue="id" 
          placeholder="Select members" 
          display="chip" 
          filter
          class="w-100"
        />
      </div>
    </div>
    <template #footer>
      <button class="btn btn-secondary me-2" @click="visible = false" :disabled="saving">Cancel</button>
      <button class="btn btn-primary" @click="save" :disabled="saving">
        <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
        Save Assignments
      </button>
    </template>
  </Dialog>
</template>

<style scoped>
:deep(.p-multiselect-label) {
  padding: 0.5rem;
}
</style>